import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import SubscriptionModal from "./SubscriptionModal";
import UserNavbar from "../../components/UserNavbar";

const PROD_API_BASE = "https://www.frendrops.in/api";

interface Product {
    _id?: string;
    id?: string;
    name: string;
    price: number;
    description?: string;
    weight?: string;
    qty?: string;
    image?: string;
    image_url?: string;
    is_subscribed?: boolean;
}

export default function ProductDetails() {
    const [showSubscription, setShowSubscription] = useState(false);
    const { productId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState<Product | null>(null);
    const [quantity, setQuantity] = useState(0);
    const [cartUpdating, setCartUpdating] = useState(false);
    const [basketItemId, setBasketItemId] = useState("");
    const [cartError, setCartError] = useState("");
    const [isSubscribed, setIsSubscribed] = useState(false);

    useEffect(() => {
        if (productId) {
            fetchProduct();
        }
    }, [productId]);

    const fetchProduct = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${PROD_API_BASE}/home/product/${productId}`
            );

            const data =
                response.data?.data ||
                response.data?.product ||
                response.data;

            setProduct(data);
            setIsSubscribed(data?.is_subscribed === true);
            await fetchCartQuantity();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCartQuantity = async () => {
        const token = localStorage.getItem("token");
        if (!token || token === "null" || token === "undefined") {
            setQuantity(0);
            return;
        }

        try {
            const { data } = await axios.get(`${PROD_API_BASE}/basket`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const payload = data?.data ?? data ?? {};
            const rows = Array.isArray(payload.orderToBePlaced) ? payload.orderToBePlaced : [];
            const row = rows.find((item: any) => {
                const id =
                    item?.product_id?._id ||
                    item?.product_id?.id ||
                    item?.product_id ||
                    item?.product?._id ||
                    item?.product?.id;
                return String(id || "") === String(productId || "");
            });
            setQuantity(Number(row?.quantity ?? row?.quantity_added ?? 0) || 0);
            setBasketItemId(String(row?.id || row?._id || row?.item_id || ""));
        } catch (error: any) {
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                setQuantity(0);
            }
        }
    };

    const updateCartQuantity = async (nextQuantity: number) => {
        const token = localStorage.getItem("token");
        if (!token || token === "null" || token === "undefined") {
            navigate("/login", { state: { from: { pathname: window.location.pathname } } });
            return;
        }

        const previous = quantity;
        try {
            setCartUpdating(true);
            setCartError("");
            setQuantity(nextQuantity);

            let response;
            if (previous === 0) {
                response = await axios.post(
                    `${PROD_API_BASE}/cart/add`,
                    { product_id: productId, quantity: nextQuantity },
                    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
                ).catch(async (error) => {
                    if (error?.response?.status !== 404) throw error;
                    return axios.post(
                        `${PROD_API_BASE}/add-to-cart`,
                        { product_id: productId, quantity: nextQuantity },
                        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
                    );
                });
            } else {
                const updateId = basketItemId || productId;
                response = await axios.put(
                    `${PROD_API_BASE}/basket/update-quantity/${encodeURIComponent(String(updateId))}`,
                    { quantity: nextQuantity },
                    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
                );
            }
            const { data } = response;
            if (data?.success === false) throw new Error(data?.message || "Unable to update basket");
            await fetchCartQuantity();
        } catch (error: any) {
            setQuantity(previous);
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                navigate("/login", { state: { from: { pathname: window.location.pathname } } });
                return;
            }
            setCartError(error?.response?.data?.message || error?.message || "Unable to update basket");
        } finally {
            setCartUpdating(false);
        }
    };

    const increaseQty = () => updateCartQuantity(quantity + 1);
    const decreaseQty = () => updateCartQuantity(Math.max(0, quantity - 1));

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-violet-200 border-t-violet-600" />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="flex h-screen items-center justify-center">
                Product not found
            </div>
        );
    }

    const image =
        product.image ||
        product.image_url ||
        "https://via.placeholder.com/600x400";

    return (
        <>
            <UserNavbar />
            <div className="min-h-screen bg-violet-50 pt-44 pb-28 px-4">
                <div className="mx-auto max-w-4xl">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="mb-6 flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-800 shadow-sm hover:bg-gray-50"
                        aria-label="Go back"
                    >
                        <ArrowLeft size={22} />
                    </button>

                    <div className="overflow-hidden rounded-3xl bg-white shadow-sm">

                        {/* Product Image */}
                        <img
                            src={image}
                            alt={product.name}
                            className="h-[400px] w-full object-cover"
                        />

                        <div className="p-6">

                            {/* Product Name */}
                            <h1 className="text-4xl font-bold text-slate-900">
                                {product.name}
                            </h1>

                            {/* Price */}
                            <div className="mt-3 text-3xl font-bold text-violet-600">
                                ₹{product.price}
                            </div>

                            {/* Weight */}
                            {(product.weight || product.qty) && (
                                <div className="mt-2 text-slate-500">
                                    {product.weight || product.qty}
                                </div>
                            )}

                            {/* Description */}
                            {product.description && (
                                <p className="mt-5 leading-7 text-slate-700">
                                    {product.description}
                                </p>
                            )}

                            {/* Subscription Banner */}
                            {isSubscribed && (
                                <div className="mt-6 flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 px-4 py-3 text-white">
                                    <span>✅</span>
                                    <span>
                                        You're subscribed — Daily Plan
                                    </span>
                                </div>
                            )}

                            {/* Subscribe Button */}
                            <button
                                onClick={() => setShowSubscription(true)}
                                className={`mt-6 w-full rounded-xl py-4 font-semibold transition ${isSubscribed
                                    ? "border-2 border-violet-600 bg-white text-violet-600"
                                    : "bg-violet-600 text-white hover:bg-violet-700"
                                    }`}
                            >
                                {isSubscribed
                                    ? "📅 Change Plan"
                                    : "📅 Subscribe"}
                            </button>

                            {cartError && <p className="mt-4 text-sm text-red-600">{cartError}</p>}

                            {/* Cart Section */}
                            <div className="mt-6 flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                                <span className="font-medium text-slate-700">
                                    Add to tomorrow's order
                                </span>

                                {quantity > 0 ? (
                                    <div className="flex overflow-hidden rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white">
                                        <button
                                            onClick={decreaseQty}
                                            disabled={cartUpdating}
                                            className="px-4 py-2 text-xl hover:bg-white/10 disabled:opacity-50"
                                        >
                                            −
                                        </button>

                                        <span className="flex min-w-[50px] items-center justify-center font-semibold">
                                            {quantity}
                                        </span>

                                        <button
                                            onClick={increaseQty}
                                            disabled={cartUpdating}
                                            className="px-4 py-2 text-xl hover:bg-white/10 disabled:opacity-50"
                                        >
                                            +
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => updateCartQuantity(1)}
                                        disabled={cartUpdating}
                                        className="rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-2 font-semibold text-white disabled:opacity-50"
                                    >
                                        {cartUpdating ? "Adding..." : "Add"}
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
                {showSubscription && product && (
                    <SubscriptionModal
                        product={{
                            _id: product._id || product.id || "",
                            name: product.name,
                            image:
                                product.image ||
                                product.image_url ||
                                "",
                            price: product.price,
                        }}
                        onClose={() =>
                            setShowSubscription(false)
                        }
                    />
                )}
            </div>
        </>
    );
}
